import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { MerchantProvider, useMerchant } from './context/MerchantContext';
import { CartProvider } from './context/CartContext';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from 'react-hot-toast';
import LoginModal from './components/LoginModal';
import ForgotPasswordModal from './components/ForgotPasswordModal';
import FirstAccessModal from './components/FirstAccessModal';
import AddressSelectionModal from './components/AddressSelectionModal';
import CheckoutModal from './components/CheckoutModal';
import PreCheckoutSuggestionsModal from './components/PreCheckoutSuggestionsModal';
import ProductModal from './components/ProductModal';
import CouponModal from './components/CouponModal';
import SalesRecoveryNotificationPrompt from './components/SalesRecoveryNotificationPrompt';
import HomePage from './pages/HomePage';
import MenuPage from './pages/MenuPage';
import TableMenuPage from './pages/TableMenuPage';
import CounterMenuPage from './pages/CounterMenuPage';
import OrderSchedulingPage from './pages/OrderSchedulingPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import PromotionsPage from './pages/PromotionsPage';
import OrdersPage from './pages/OrdersPage';
import ProfilePage from './pages/ProfilePage';
import CashbackPage from './pages/CashbackPage';
import ReviewPage from './pages/ReviewPage';
import TrackingPage from './pages/TrackingPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import { trackPageView } from './services/analyticsService';
import './App.css';

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    document.getElementById('root')?.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const AnalyticsTracker = () => {
  const location = useLocation();

  useEffect(() => {
    trackPageView(`${location.pathname}${location.search}`);
  }, [location.pathname, location.search]);

  return null;
};

const FaviconUpdater: React.FC = () => {
  const { restaurant } = useMerchant();

  useEffect(() => {
    if (restaurant?.logo) {
      const link: HTMLLinkElement | null = document.querySelector("link[rel*='icon']");
      if (link) {
        link.href = restaurant.logo;
      } else {
        const newLink = document.createElement('link');
        newLink.rel = 'icon';
        newLink.href = restaurant.logo;
        document.getElementsByTagName('head')[0].appendChild(newLink);
      }
      
      // Also update apple-touch-icon if it exists
      const appleLink: HTMLLinkElement | null = document.querySelector("link[rel='apple-touch-icon']");
      if (appleLink) {
        appleLink.href = restaurant.logo;
      }
    }
  }, [restaurant]);

  return null;
};

const RootRoute: React.FC = () => {
  const location = useLocation();
  const token = new URLSearchParams(location.search).get('token');

  return token ? <ReviewPage /> : <MenuPage />;
};

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      <Route path="/" element={<RootRoute />} />
      <Route path="/home" element={<HomePage />} />
      <Route path="/menu" element={<MenuPage />} />
      <Route path="/menu/:restaurantId" element={<MenuPage />} />
      <Route path="/promocoes" element={<PromotionsPage />} />
      <Route path="/pedidos" element={<OrdersPage />} />
      <Route path="/perfil" element={<ProfilePage />} />
      <Route path="/cashback" element={<CashbackPage />} />
      <Route path="/avaliar/:token" element={<ReviewPage />} />
      <Route path="/:slug/avaliar/:token" element={<ReviewPage />} />
      <Route path="/rastreio/:token" element={<TrackingPage />} />
      <Route path="/:slug/rastreio/:token" element={<TrackingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/registrar" element={<RegisterPage />} />
      <Route path="/table-menu/:restaurantId/:tableId" element={<TableMenuPage />} />
      <Route path="/counter-menu/:restaurantId" element={<CounterMenuPage />} />
      <Route path="/schedule-order/:restaurantId" element={<OrderSchedulingPage />} />
      <Route path="/order-success" element={<OrderSuccessPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  const { 
    isCouponModalOpen, 
    setIsCouponModalOpen, 
    productModalData, 
    setProductModalData,
    showAddressModal,
    isCheckoutModalOpen,
    isPreCheckoutSuggestionsModalOpen
  } = useMerchant();

  const { showLoginModal, showForgotPasswordModal, showFirstAccessModal } = useAuth();

  // Block scroll when any modal is open
  useEffect(() => {
    const isAnyModalOpen = isCouponModalOpen ||
                          !!productModalData ||
                          showAddressModal ||
                          isCheckoutModalOpen ||
                          isPreCheckoutSuggestionsModalOpen ||
                          showLoginModal ||
                          showForgotPasswordModal ||
                          showFirstAccessModal;

    const rootElement = document.getElementById('root');

    if (isAnyModalOpen) {
      if (rootElement) rootElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      if (rootElement) rootElement.style.overflow = 'auto';
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    }

    return () => {
      if (rootElement) rootElement.style.overflow = 'auto';
    };
  }, [isCouponModalOpen, productModalData, showAddressModal, isCheckoutModalOpen, isPreCheckoutSuggestionsModalOpen, showLoginModal, showForgotPasswordModal, showFirstAccessModal]);

  return (
    <Router>
      <ScrollToTop />
      <AnalyticsTracker />
      <Toaster 
        position="top-right" 
        reverseOrder={false} 
        containerStyle={{ zIndex: 9999999 }}
      />
      <FaviconUpdater />
      <AppRoutes />
      <LoginModal />
      <ForgotPasswordModal />
      <FirstAccessModal />
      <AddressSelectionModal />
      <CheckoutModal />
      <PreCheckoutSuggestionsModal />
      <SalesRecoveryNotificationPrompt />
      
      {/* Global Product Modal */}
      {productModalData && (
        <ProductModal
          item={productModalData.item}
          isOpen={true}
          onClose={() => setProductModalData(null)}
          editMode={productModalData.editMode}
          editIndex={productModalData.editIndex}
        />
      )}

      {/* Global Coupon Modal */}
      <CouponModal
        isOpen={isCouponModalOpen}
        onClose={() => setIsCouponModalOpen(false)}
      />
    </Router>
  );
}

function Root() {
  return (
    <MerchantProvider>
      <ThemeProvider>
        <AuthProvider>
          <CartProvider>
            <App />
          </CartProvider>
        </AuthProvider>
      </ThemeProvider>
    </MerchantProvider>
  );
}

export default Root;
